import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { SFetchKoboData } from "../../../../apis/app.service";
import { IKoboProject } from "../../core/models";
import LoadingSpinner from "../../../sharedcomponents/LoadingSpinner";
import AnalyticsDatePicker from "../../../Analytics/components/AnalyticsDatePicker";
import moment from "moment";
import dayjs from "dayjs";
import "./ViewKoboData.scss";

interface ViewKoboDataProps {
  project: IKoboProject;
  onClose: () => void;
}

const ViewKoboData: React.FC<ViewKoboDataProps> = ({ project, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Date picker state
  const [dateType, setDateType] = useState("month");
  const [selectedDate, setSelectedDate] = useState<any>(dayjs());
  const [selectedDateFormatted, setSelectedDateFormatted] = useState(
    dayjs().format("YYYY-MM")
  );
  const [startDate, setStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    dayjs().endOf("month").format("YYYY-MM-DD")
  );

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const {
    data: koboData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["koboData", project._id, currentPage, startDate, endDate],
    queryFn: () => {
      const params: any = {
        start: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      // Add date filters
      const dateQuery: any = {
        _submission_time: {
          $gte: moment(startDate).startOf("day").toISOString(),
          $lte: moment(endDate).endOf("day").toISOString(),
        },
      };

      // Kobo API expects query as a JSON string
      params.query = JSON.stringify(dateQuery);

      return SFetchKoboData(project._id, params);
    },
  });

  // Extract column headers from the first submission
  const columns = useMemo(() => {
    if (!koboData?.data.data.results || koboData.data.data.results.length === 0)
      return [];

    const systemFields = [
      "_id",
      "_uuid",
      "_validation_status",
      "_notes",
      "_status",
      "__version__",
      "formhub/uuid",
      "meta/instanceID",
    ];

    const firstSubmission = koboData.data.data.results[0];
    return Object.keys(firstSubmission).filter(
      (key) => !systemFields.includes(key) && !key.startsWith("_")
    );
  }, [koboData]);

  const handleClearFilters = () => {
    const now = dayjs();
    const monthStart = now.startOf("month").format("YYYY-MM-DD");
    const monthEnd = now.endOf("month").format("YYYY-MM-DD");

    setDateType("month");
    setSelectedDate(now);
    setSelectedDateFormatted(now.format("YYYY-MM"));
    setStartDate(monthStart);
    setEndDate(monthEnd);
    setCurrentPage(1);
  };

  const formatCellValue = (value: any) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") return JSON.stringify(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    if (
      !koboData?.data.data.results ||
      koboData.data.data.results.length === 0
    ) {
      alert("No data to download");
      return;
    }

    // Create CSV headers
    const headers = ["#", "Submission Time", "Submitted By", ...columns];
    const csvContent = [
      headers.join(","),
      ...koboData.data.data.results.map((submission: any, index: number) => {
        const row = [
          (currentPage - 1) * itemsPerPage + index + 1,
          submission._submission_time
            ? moment(submission._submission_time).format("YYYY-MM-DD HH:mm")
            : "-",
          `"${(submission._submitted_by || "-").replace(/"/g, '""')}"`,
          ...columns.map((col) => {
            const value = formatCellValue(submission[col]);
            // Escape commas and quotes in CSV
            return `"${value.replace(/"/g, '""')}"`;
          }),
        ];
        return row.join(",");
      }),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${project.name.replace(/\s+/g, "_")}_${moment().format(
        "YYYY-MM-DD"
      )}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = () => {
    if (
      !koboData?.data.data.results ||
      koboData.data.data.results.length === 0
    ) {
      alert("No data to download");
      return;
    }

    // Create a printable HTML table
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow pop-ups to download PDF");
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${project.name} - Data Export</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; margin-bottom: 10px; }
            .info { color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #1976d2; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            @media print {
              body { padding: 10px; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          <h1>${project.name} - Data Export</h1>
          <div class="info">
            <p>Total Submissions: ${koboData.data.data.count || 0}</p>
            <p>Export Date: ${moment().format("YYYY-MM-DD HH:mm")}</p>
            ${startDate ? `<p>Start Date: ${startDate}</p>` : ""}
            ${endDate ? `<p>End Date: ${endDate}</p>` : ""}
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Submission Time</th>
                <th>Submitted By</th>
                ${columns.map((col) => `<th>${col}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${koboData.data.data.results
                .map(
                  (submission: any, index: number) => `
                <tr>
                  <td>${(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>${
                    submission._submission_time
                      ? moment(submission._submission_time).format(
                          "YYYY-MM-DD HH:mm"
                        )
                      : "-"
                  }</td>
                  <td>${submission._submitted_by || "-"}</td>
                  ${columns
                    .map(
                      (col) => `<td>${formatCellValue(submission[col])}</td>`
                    )
                    .join("")}
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content view-data-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2>{project.name} - Data</h2>
            {koboData && (
              <p className="data-count">
                Total Submissions: {koboData.data.data.count || 0}
              </p>
            )}
          </div>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <label>Date Range:</label>
            <AnalyticsDatePicker
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              selectedDateFormatted={selectedDateFormatted}
              setSelectedDateFormatted={setSelectedDateFormatted}
              type={dateType}
              setType={setDateType}
              onDateChange={handleDateChange}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="pageSize">Rows per page:</label>
            <select
              id="pageSize"
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="page-size-select"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={70}>70</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="filter-actions">
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
          <div className="download-actions">
            <button
              className="btn btn-success btn-sm"
              onClick={downloadCSV}
              disabled={
                !koboData?.data.data.results ||
                koboData.data.data.results.length === 0
              }
            >
              <i className="bx bx-download"></i> CSV
            </button>
            <button
              className="btn btn-success btn-sm"
              onClick={downloadPDF}
              disabled={
                !koboData?.data.data.results ||
                koboData.data.data.results.length === 0
              }
            >
              <i className="bx bx-download"></i> PDF
            </button>
          </div>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="error-state">
              <p>Error loading data: {(error as Error).message}</p>
            </div>
          ) : koboData?.data.data.results &&
            koboData.data.data.results.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Submission Time</th>
                    <th>Submitted By</th>
                    {columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {koboData.data.data.results.map(
                    (submission: any, index: number) => (
                      <tr key={submission._id || index}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>
                          {submission._submission_time
                            ? moment(submission._submission_time).format(
                                "YYYY-MM-DD HH:mm"
                              )
                            : "-"}
                        </td>
                        <td>{submission._submitted_by || "-"}</td>
                        {columns.map((col) => (
                          <td key={col}>{formatCellValue(submission[col])}</td>
                        ))}
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No submissions found for this project.</p>
            </div>
          )}
        </div>

        {koboData?.data.data.count &&
          koboData.data.data.count > itemsPerPage && (
            <div className="modal-footer">
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of{" "}
                  {Math.ceil(koboData.data.data.count / itemsPerPage)}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={
                    !koboData.data.data.next ||
                    currentPage >=
                      Math.ceil(koboData.data.data.count / itemsPerPage)
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ViewKoboData;
